<template>
    <div class="solutionguide__decorfinder">
        <component :is="wrapperComponentName" @close="abort">
            <template slot="header">
                <div class="epf-filter-decor__tab-links">
                    <button class="epf-filter-decor__tab-link" :class="{'epf-filter-decor__tab-link--active': isModeText}" @click="mode = 'text'">
                        {{ $t('filter.decor.byKeyword') }}
                    </button>
                    <button class="epf-filter-decor__tab-link" :class="{'epf-filter-decor__tab-link--active': isModeFilter}" @click="mode = 'filter'">
                        {{ $t('filter.decor.byFilter') }}
                    </button>
                </div>

                <div class="row" v-show="isModeText">
                    <div class="epf-filter__input col-12 col-md-6 col-lg-4">
                        <div class="epf-filter__title">{{ $t('filter.decor.title') }}</div>
                        <input class="search__field" type='text' maxlength='100' :placeholder="$t('filter.decor.keyword.placeholder')" v-model="text_query"
                               :disabled="freeze">
                    </div>
                </div>
                <div class="row" v-show="isModeFilter">
                    <decorfinder-filter-select v-if="filterColor" :filter-config="filterColor" :available-option-values="filterColorAvailableValues"
                                               :freeze="freeze" v-model="selectedColor" additional-class="col-12 col-md-4 col-lg-3"/>
                    <decorfinder-filter-select v-if="filterGroup" :filter-config="filterGroup" :available-option-values="filterGroupAvailableValues"
                                               :freeze="freeze" v-model="selectedGroup" additional-class="col-12 col-md-4 col-lg-3"/>
                    <decorfinder-filter-select v-if="filterWood" :filter-config="filterWood" :available-option-values="filterWoodAvailableValues"
                                               :freeze="freeze" v-model="selectedWood" additional-class="col-12 col-md-4 col-lg-3"/>
                    <decorfinder-filter-select v-if="filterStone" :filter-config="filterStone" :available-option-values="filterStoneAvailableValues"
                                               :freeze="freeze" v-model="selectedStone" additional-class="col-12 col-md-4 col-lg-3"/>
                </div>
            </template>
            <template slot="body">
                <div class="row">
                    <div class="col-12">
                        <global-loading-overlay :showLoading="freeze || text_freeze">
                            {{ $t('filter.decor.loading') }}
                        </global-loading-overlay>
                        <div v-if="text_result && availableDecors.length === 0">
                            {{ $t('filter.decor.keyword.noResult') }}
                        </div>
                        <template v-else>
                            <div class="row">
                                <div class="col-12">
                                    {{ availableDecors.length }} {{ $t('filter.decor.totalDecors') }}
                                </div>
                            </div>
                            <div class="row">
                                <template v-for="(chunk, chunkIndex) in splittedAvailableDecors">
                                    <div v-if="page >= chunkIndex" v-for="(decor, index) in chunk"
                                         class="col-4 col-md-2 col-xl-1 decor__col"
                                         :class="['group--' + groupTiles(index + chunkIndex * chunkSize), index + chunkIndex * chunkSize === selectedDecorIndex ? 'decor__col--selected':'']"
                                         v-show="page === groupTiles(index + chunkIndex * chunkSize)"
                                    >
                                        <div class="decor__col--icon icon-item icon-item--ghost">
                                            <span class="icon icon--check-square"/>
                                        </div>
                                        <global-material-tile :entity="decor" @click="selectedDecorIndex = index + chunkIndex * chunkSize"/>
                                    </div>
                                </template>
                            </div>
                        </template>
                    </div>
                </div>

            </template>
            <template slot="pagination">
                <span class="pagination__indicator">{{ decorsShown }}</span>
                <global-button icon="arrow-left" @click="changePage(page-1)" theme="secondary" :class="page-1 < lowerEnd ? 'disabled': ''"
                               class="ignore-context pagination--prev"/>
                <global-button icon="arrow-right" @click="changePage(page+1)" theme="secondary" :class="page+1 > upperEnd ? 'disabled': ''"
                               class="ignore-context pagination--prev"/>
            </template>
            <template slot="controls__left">
                <global-button theme="secondary" @click="abort">{{ $t('filter.decor.close') }}</global-button>
            </template>
            <template slot="controls__right">
                <global-button theme="primary" @click="saveAndClose">{{ $t('filter.decor.select') }}</global-button>
            </template>
        </component>
    </div>
</template>

<script lang="ts">
import Vue from "vue"
import GlobalButton from "../../../../../global/global-elements/global-button/global-button.vue"
import GlobalModal from "../../../../../global/global-elements/global-modal/global-modal.vue"
import EpfFilterSelect from "../../../Partials/PimEndproduktFinder/components/epf-filter-select/epf-filter-select.vue"
import GlobalLoadingOverlay from "../../../../../global/global-elements/global-loading-overlay/global-loading-overlay.vue"
import GlobalMaterialTile from "../../../../../global/global-elements/global-material-tile/global-material-tile.vue"
import DecorfinderWrapper from "./decorfinder-wrapper.vue"
import type {SolutionGuide} from "../../../../../_types/solutionguide"
import type {Productfinder} from "../../../../../_types/productfinder"
import {detectBreakpoint} from "../../../../../utilities/sal-breakpoint/sal-breakpoint"
import DecorfinderFilterSelect from "./decorfinder-filter-select.vue"
import {merge} from "lodash";

const tilesPerPageConfiguration = {
    'xs': null,
    'sm': null,
    'md': 12,
    'lg': 6,
    'xl': 24
}

interface ConfigApiResponse {
    initialData: Productfinder.Data
    config: Productfinder.Config
}

const STORAGE_PREFIX = '2'; // Change if data format changes

export default Vue.extend({
    name: "decorfinder",
    components: {DecorfinderFilterSelect, DecorfinderWrapper, GlobalMaterialTile, GlobalLoadingOverlay, EpfFilterSelect, GlobalModal, GlobalButton},
    props: {
        displayMode: { // inline or modal
            type: String,
            default: 'inline'
        },
        baseProducts: {
            type: Array as () => SolutionGuide.DekorativePlatte[],
        }
    },
    data(): {
        chunkSize: number
        isModal: boolean,
        config: Productfinder.Config | null,
        initialData: Productfinder.Data | null,
        lockedBy: string[],
        mode: 'text' | 'filter',
        page: number,
        tilesPerPage: number,
        selectedDecorIndex: number | null,
        selectedColor: string | null,
        selectedGroup: string | null,
        selectedWood: string | null,
        selectedStone: string | null,
        text_query: string,
        text_result: string[] | null,
        text_freeze: boolean,
        text_timeout: number | undefined,
        text_controller: AbortController | null
    } {
        return {
            chunkSize: 24,
            isModal: this.displayMode === 'modal',
            config: null,
            initialData: null,
            lockedBy: [],

            mode: 'text',

            page: 0,
            tilesPerPage: 12,

            selectedDecorIndex: null,
            selectedColor: null,
            selectedGroup: null,
            selectedWood: null,
            selectedStone: null,

            text_query: '',
            text_result: null,
            text_freeze: false,
            text_timeout: undefined,
            text_controller: null
        }
    },
    watch: {
        selectedDecor() {
            if (!this.isModal) {
                this.$emit('change', this.selectedDecor)
            }
        },
        baseProducts() {
            this.loadConfig()
        },
        mode: function (newMode) {
            if (newMode === 'text') {
                this.selectedDecorIndex = null
                this.selectedColor = null
                this.selectedGroup = null
                this.selectedWood = null
                this.selectedStone = null
            } else if (newMode === 'filter') {
                this.text_query = '';
                this.text_result = null;
                this.text_freeze = false;
                if (this.text_timeout) {
                    clearTimeout(this.text_timeout);
                    this.text_timeout = undefined;
                }
            }
        },
        availableDecors: function () {
            this.page = 0;
            this.selectedDecorIndex = null;

            if (!tilesPerPageConfiguration[detectBreakpoint()]) {
                this.tilesPerPage = this.availableDecors.length
            }
        },
        text_query: function (newVal) {
            if (this.text_timeout) {
                clearTimeout(this.text_timeout);
            }
            if (newVal && newVal.length > 0) {
                this.text_timeout = setTimeout(() => {
                    if (this.text_controller) {
                        this.text_controller.abort();
                    }
                    this.text_freeze = true;
                    this.text_controller = new AbortController();
                    fetch(window.SOLUTIONGUIDE_DECOR_SEARCH_API + '&tx_salbase9_search[q]=' + encodeURIComponent(newVal),
                        {signal: this.text_controller.signal}
                    )
                        .then((response) => response.json())
                        .then((json) => {
                            if (json.decor && Array.isArray(json.decor.docs)) {
                                this.text_result = json.decor.docs;
                            } else {
                                this.text_result = null;
                            }
                            this.text_timeout = undefined;
                            this.text_freeze = false;
                        })
                        .catch(e => {/* In most cases, the request was just aborted */
                        });
                }, 250);
            } else {
                this.text_result = null;
                this.text_timeout = undefined;
            }
        }
    },
    computed: {
        splittedAvailableDecors() {
            const chunks = [];

            for (let i = 0; i < this.availableDecors.length; i += this.chunkSize) {
                const chunk = this.availableDecors.slice(i, i + this.chunkSize);
                chunks.push(chunk)
            }

            return chunks;
        },
        wrapperComponentName(): string {
            return this.isModal ? 'global-modal' : 'decorfinder-wrapper'
        },
        freeze(): boolean {
            return this.lockedBy.length > 0
        },
        isModeText(): boolean {
            return this.mode === 'text'
        },
        isModeFilter(): boolean {
            return this.mode === 'filter'
        },
        decorsShown(): string {
            return ((this.page * this.tilesPerPage) + 1) + ' - ' + (((this.page * this.tilesPerPage + this.tilesPerPage) > this.availableDecors.length) ? this.availableDecors.length : this.page * this.tilesPerPage + this.tilesPerPage)
        },
        upperEnd(): number {
            return Math.ceil(this.availableDecors.length / this.tilesPerPage) - 1
        },
        lowerEnd(): number {
            return 0
        },
        filterColor(): Productfinder.Filter | null {
            const config = this.config?.filters.find(filter => filter.name === 'color') || null
            return config && config.options.length > 0 ? config : null
        },
        filterGroup(): Productfinder.Filter | null {
            const config = this.config?.filters.find(filter => filter.name === 'group') || null
            return config && config.options.length > 0 ? config : null
        },
        filterWood(): Productfinder.Filter | null {
            const config = this.config?.filters.find(filter => filter.name === 'wood') || null
            return config && config.options.length > 0 ? config : null
        },
        filterStone(): Productfinder.Filter | null {
            const config = this.config?.filters.find(filter => filter.name === 'stone') || null
            return config && config.options.length > 0 ? config : null
        },
        filterColorAvailableValues(): string[] {
            const availableOptions = this.availableDecors.flatMap(decor => decor.filterValues['color'])
            return this.filterColor?.options
                .filter((option: Productfinder.FilterOption) => {
                    const searchValue = typeof option !== 'string' ? option.value : option;
                    if (searchValue.indexOf('~') >= 0) {
                        const searchValues = searchValue.split('~')
                        return searchValues.some(searchValue => availableOptions.includes(searchValue))
                    }
                    return availableOptions.includes(searchValue)
                })
                .map(option => typeof option !== 'string' ? option.value : option) || []
        },
        filterGroupAvailableValues(): string[] {
            const availableOptions = this.availableDecors.flatMap(decor => decor.filterValues['group'])
            return this.filterGroup?.options
                .filter((option: Productfinder.FilterOption) => availableOptions.includes(typeof option !== 'string' ? option.value : option))
                .map(option => typeof option !== 'string' ? option.value : option) || []
        },
        filterWoodAvailableValues(): string[] {
            const availableOptions = this.availableDecors.flatMap(decor => decor.filterValues['wood'])
            return this.filterWood?.options
                .filter((option: Productfinder.FilterOption) => availableOptions.includes(typeof option !== 'string' ? option.value : option))
                .map(option => typeof option !== 'string' ? option.value : option) || []

        },
        filterStoneAvailableValues(): string[] {
            const availableOptions = this.availableDecors.flatMap(decor => decor.filterValues['stone'])
            return this.filterStone?.options
                .filter((option: Productfinder.FilterOption) => availableOptions.includes(typeof option !== 'string' ? option.value : option))
                .map(option => typeof option !== 'string' ? option.value : option) || []
        },

        availableDecors(): Productfinder.Product[] {
            let availableDecors = this.initialData?.entities || []

            if (this.mode === 'filter') {
                availableDecors = availableDecors.filter(product => {
                    return (this.selectedColor === null || this.selectedColor.split('~').some(color => product.filterValues['color'] && product.filterValues['color'].includes(color))) &&
                        (this.selectedGroup === null || product.filterValues['group'] && product.filterValues['group'].includes(this.selectedGroup)) &&
                        (this.selectedWood === null || product.filterValues['wood'] && product.filterValues['wood'].includes(this.selectedWood)) &&
                        (this.selectedStone === null || product.filterValues['stone'] && product.filterValues['stone'].includes(this.selectedStone))
                })
            } else if (this.mode === 'text' && Array.isArray(this.text_result)) {
                availableDecors = this.text_result.reduce(function (result: Productfinder.Product[], foundId: string): Productfinder.Product[] {
                    const foundDecor = availableDecors.find(product => foundId === product.pim_id)
                    if (foundDecor) {
                        result.push(foundDecor)
                    }
                    return result
                }, [])
            }

            return availableDecors
        },
        selectedDecor(): Productfinder.Product | null {
            if (this.selectedDecorIndex === null || this.selectedDecorIndex < 0 || this.selectedDecorIndex >= this.availableDecors.length) {
                return null
            }
            return this.availableDecors[this.selectedDecorIndex]
        },
    },
    methods: {
        lockWithKey(key: string) {
            this.lockedBy.push(key)
        },
        unlockWithKey(key: string) {
            this.lockedBy.splice(this.lockedBy.indexOf(key), 1)
        },
        isLockedWithKey(key: string) {
            return this.lockedBy.indexOf(key) !== -1
        },
        loadConfig() {
            this.baseProducts?.forEach(baseProduct => {
                if (this.isLockedWithKey(baseProduct.pim_id) || !baseProduct) {
                    return
                }
                this.lockWithKey(baseProduct.pim_id)

                // Fetch Configuration from configApi of result
                const storedResponse = sessionStorage.getItem(STORAGE_PREFIX + baseProduct.configApi);
                if (storedResponse) {
                    const responseData = JSON.parse(storedResponse) as ConfigApiResponse


                    if (this.config && responseData.config) {
                        this.config = merge({}, this.config, responseData.config)
                    } else {
                        this.config = responseData.config
                    }

                    if (this.initialData && responseData.initialData) {
                        this.initialData = merge({}, this.initialData, responseData.initialData)
                    } else {
                        this.initialData = responseData.initialData
                    }

                    // this.config = responseData.config
                    // this.initialData = responseData.initialData
                    this.unlockWithKey(baseProduct.pim_id)
                    return
                }

                fetch(baseProduct.configApi)
                    .then(response => response.json())
                    .then((responseData: ConfigApiResponse) => {
                        if (responseData && responseData.config) {
                            sessionStorage.setItem(STORAGE_PREFIX + baseProduct.configApi, JSON.stringify(responseData));
                        } else {
                            this.unlockWithKey(baseProduct.pim_id)
                            return
                        }

                        if (this.config) {
                            this.config = merge(this.config, responseData.config)
                        } else {
                            this.config = responseData.config
                        }

                        if (this.initialData) {
                            this.initialData = merge(this.initialData, responseData.initialData)
                        } else {
                            this.initialData = responseData.initialData
                        }

                        // this.config = responseData.config
                        // this.initialData = responseData.initialData
                        this.unlockWithKey(baseProduct.pim_id)
                    })
                    .catch(error => {
                        this.unlockWithKey(baseProduct.pim_id)
                    })
            })
        },

        abort() {
            this.selectedDecorIndex = null
            this.text_query = ''

            if (this.isModal) {
                this.$emit('close')
            }
        },
        saveAndClose(): void {
            this.text_query = ''

            if (this.isModal) {
                this.$emit('save', this.selectedDecor)
            }
        },
        groupTiles(index: number): number {
            return Math.floor(index / this.tilesPerPage)
        },
        changePage(index: number): void {
            let normalizedPage = index < this.lowerEnd ? this.lowerEnd : index
            normalizedPage = index > this.upperEnd ? this.upperEnd : normalizedPage
            this.page = normalizedPage
        }
    },
    mounted() {
        this.tilesPerPage = (tilesPerPageConfiguration[detectBreakpoint()] ? tilesPerPageConfiguration[detectBreakpoint()] : this.availableDecors.length) || 12

        if (tilesPerPageConfiguration[detectBreakpoint()] === null) {
            this.chunkSize = 300
        }

        // if (tilesPerPageConfiguration[detectBreakpoint()] === null) {
        //     window.setTimeout(() => {
        //         const observer = new IntersectionObserver((entries, observer) => {
        //             entries.forEach(entry => {
        //                 observer.unobserve(entry.target)
        //             })
        //
        //             if (this.page < (this.availableDecors.length / this.chunkSize)) {
        //                 this.page = this.page + 1
        //             }
        //
        //             const decors = document.querySelectorAll(".decor__col")
        //             if (decors) {
        //                 observer.observe(decors[decors.length - 1])
        //             }
        //         })
        //         const decors = document.querySelectorAll(".decor__col")
        //         if (decors.length) {
        //             observer.observe(decors[decors.length - 1])
        //         }
        //     }, 1000)
        // }

        window.addEventListener('resize', () => {
            const oldTilePage = this.tilesPerPage

            this.tilesPerPage = (tilesPerPageConfiguration[detectBreakpoint()] ? tilesPerPageConfiguration[detectBreakpoint()] : this.availableDecors.length) || 12
            this.changePage(Math.floor(this.page * oldTilePage / this.tilesPerPage))
        })

        this.loadConfig()
    }
})
</script>
